/**
 * Client messaging + portal presence — one store, because they are one poll.
 *
 * Backend: `app/api/v1/client_messages_portal.py` (see
 * `services/clientMessages.service.ts` for the route contract).
 *
 * ## Presence: why a heartbeat, and why it must stop
 *
 * `POST /v1/client/messages/heartbeat` bumps `users.last_seen_at`; ops calls
 * someone "online" when that column is inside
 * `presence.PORTAL_ONLINE_WINDOW_MINUTES`. Polling, never a socket — the ground
 * rule from the design, and the reason a dropped connection cannot leave a
 * carrier looking permanently present.
 *
 * **A dot that stays green after the client leaves is worse than no dot**,
 * because a consultant rings someone who is not there and gets voicemail while
 * the screen says they are reading. So the heartbeat stops the moment the tab
 * stops being looked at:
 *
 *   - `visibilitychange` → hidden: interval cleared immediately (a background
 *     tab is not a person);
 *   - `pagehide` / `beforeunload`: cleared (covers close, navigate-away, and
 *     bfcache suspension, which `beforeunload` alone misses on mobile Safari);
 *   - the layout unmounting (logout, route out of the portal shell): cleared.
 *
 * Returning to the tab fires one heartbeat immediately rather than waiting out
 * the interval, so the dot comes back at once rather than up to a minute later.
 *
 * There is no "I am leaving" call, and deliberately so: a beacon fired on
 * unload would be one more endpoint that can be spoofed or missed, and the
 * window already expires on its own. The honest consequence — a dot can stay
 * lit for up to the online window after a client closes the tab — is why the
 * ops surfaces label the strip with the real window instead of the word "now".
 *
 * ## Interval
 *
 * 60s, well inside the 5-minute window (the server also coalesces writes at 30s,
 * so a faster poll would buy nothing and cost request volume). The window
 * itself is never hardcoded here — it arrives in the heartbeat response.
 */
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import {
  clientMessagesService,
  internalNoteLeakRef,
  type ClientThreadMessage,
  type ResponseExpectation,
} from '../services/clientMessages.service'

/** How often the portal pings while the tab is visible. */
export const HEARTBEAT_INTERVAL_MS = 60_000

/** Statuses that mean "stop asking" rather than "try again in a minute". */
const TERMINAL_STATUSES = new Set([401, 403, 404])

export const useClientMessagesStore = defineStore('clientMessages', () => {
  const messages = ref<ClientThreadMessage[]>([])
  const threadId = ref<string | null>(null)
  const clientName = ref<string | null>(null)
  const expectation = ref<ResponseExpectation | null>(null)
  const unreadCount = ref(0)
  /** From the server; null until the first heartbeat answers. */
  const onlineWindowMinutes = ref<number | null>(null)

  const loading = ref(false)
  const sending = ref(false)
  const error = ref<string | null>(null)
  /** Set when the heartbeat gave up (not linked to a client, or not a client). */
  const presenceStopped = ref<string | null>(null)

  let timer: ReturnType<typeof setInterval> | null = null
  let listening = false

  const hasThread = computed(() => threadId.value !== null)
  /** Surfaced by the view: a server that leaked an internal note is a P0.
   *  Reads the service's REF — a plain function call would cache null forever. */
  const leak = computed(() => internalNoteLeakRef.value)

  // ── Thread ────────────────────────────────────────────────────────────────

  async function loadThread() {
    loading.value = true
    error.value = null
    try {
      const thread = await clientMessagesService.getThread()
      // Already sanitized at the service boundary.
      messages.value = thread.messages
      threadId.value = thread.thread_id
      clientName.value = thread.client_name
      expectation.value = thread.response_expectation
      unreadCount.value = thread.unread_count
    } catch (e: unknown) {
      error.value = readableError(e)
    } finally {
      loading.value = false
    }
  }

  async function send(body: string) {
    const text = body.trim()
    if (!text || sending.value) return false
    sending.value = true
    error.value = null
    try {
      const created = await clientMessagesService.post(text)
      if (created) messages.value = [...messages.value, created]
      // The first message mints the thread; re-read so `threadId` and the
      // empty state stop disagreeing with what is on screen.
      if (!threadId.value) await loadThread()
      return true
    } catch (e: unknown) {
      error.value = readableError(e)
      return false
    } finally {
      sending.value = false
    }
  }

  /** Mark the consultant's messages read — called when the thread is on screen. */
  async function markRead() {
    if (!threadId.value || unreadCount.value === 0) return
    try {
      const result = await clientMessagesService.markRead()
      unreadCount.value = result.unread_count
      const now = new Date().toISOString()
      messages.value = messages.value.map((m) =>
        m.sender_side === 'staff' && !m.read_at ? { ...m, read_at: now } : m,
      )
    } catch {
      /* A failed read-marker is cosmetic; the badge corrects on the next poll. */
    }
  }

  // ── Presence ──────────────────────────────────────────────────────────────

  async function beat() {
    try {
      const result = await clientMessagesService.heartbeat()
      onlineWindowMinutes.value = result.online_window_minutes
      const previous = unreadCount.value
      unreadCount.value = result.unread_count
      // New consultant message while the thread is open → pull it in, so the
      // client does not have to reload to read what the badge just announced.
      if (result.unread_count > previous && !loading.value) await loadThread()
    } catch (e: unknown) {
      const status = statusOf(e)
      if (status !== null && TERMINAL_STATUSES.has(status)) {
        // 403 = a staff token on the portal surface; 404 = a login not linked
        // to a client. Neither improves by retrying every minute for hours.
        presenceStopped.value =
          status === 404
            ? 'This login is not linked to a company account yet.'
            : 'This account cannot use the portal message thread.'
        stopHeartbeat()
      }
      /* Anything else (offline, 500, timeout) keeps polling — it may recover. */
    }
  }

  function startInterval() {
    if (timer || presenceStopped.value) return
    timer = setInterval(() => void beat(), HEARTBEAT_INTERVAL_MS)
  }

  function clearInterval_() {
    if (timer) {
      clearInterval(timer)
      timer = null
    }
  }

  function onVisibility() {
    if (typeof document === 'undefined') return
    if (document.hidden) {
      // Hidden tab: stop immediately. Nobody is on the other end of it.
      clearInterval_()
    } else if (!presenceStopped.value) {
      void beat()
      startInterval()
    }
  }

  function onHide() {
    clearInterval_()
  }

  /**
   * Begin heartbeating. Idempotent — call it from the portal shell's mount.
   *
   * `enabled` is the caller's role gate: only a real CLIENT token can use this
   * surface, and a staff token would 403 on every ping. Passing `false` is a
   * no-op, not an error.
   */
  function startHeartbeat(enabled = true) {
    if (!enabled || presenceStopped.value) return
    if (typeof document !== 'undefined' && !listening) {
      document.addEventListener('visibilitychange', onVisibility)
      window.addEventListener('pagehide', onHide)
      window.addEventListener('beforeunload', onHide)
      listening = true
    }
    if (typeof document !== 'undefined' && document.hidden) return
    void beat()
    startInterval()
  }

  function stopHeartbeat() {
    clearInterval_()
    if (listening && typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', onVisibility)
      window.removeEventListener('pagehide', onHide)
      window.removeEventListener('beforeunload', onHide)
      listening = false
    }
  }

  /** Clear every trace of the previous session (logout, client switch). */
  function reset() {
    stopHeartbeat()
    messages.value = []
    threadId.value = null
    clientName.value = null
    expectation.value = null
    unreadCount.value = 0
    onlineWindowMinutes.value = null
    error.value = null
    presenceStopped.value = null
  }

  return {
    messages,
    threadId,
    clientName,
    expectation,
    unreadCount,
    onlineWindowMinutes,
    loading,
    sending,
    error,
    presenceStopped,
    hasThread,
    leak,
    loadThread,
    send,
    markRead,
    startHeartbeat,
    stopHeartbeat,
    reset,
  }
})

function statusOf(e: unknown): number | null {
  const status = (e as { response?: { status?: number } })?.response?.status
  return typeof status === 'number' ? status : null
}

function readableError(e: unknown): string {
  const detail = (e as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail
  if (typeof detail === 'string' && detail) return detail
  return 'We could not reach your message thread. Please try again in a moment.'
}
